import { useCallback, useEffect, useRef, useState } from 'react';
import { apolloClient } from '../api/apolloClient';
import { useSocket } from '../context/WebsocketContext';
import { ScoreUpdateData } from '../interfaces/websocket.interface';

interface UseStoryScoreUpdatesOptions {
  storyId?: string;
  onUpdate?: (data: ScoreUpdateData) => void;
}

export const useStoryScoreUpdates = (options?: UseStoryScoreUpdatesOptions) => {
  const { scoreUpdates, onScoreUpdate } = useSocket();
  const [latestUpdate, setLatestUpdate] = useState<ScoreUpdateData | null>(
    null,
  );

  // Stable ref for the callback — avoids re-subscribing on every render
  const onUpdateRef = useRef(options?.onUpdate);
  onUpdateRef.current = options?.onUpdate;

  const storyIdRef = useRef(options?.storyId);
  storyIdRef.current = options?.storyId;

  // Get initial value if available
  useEffect(() => {
    if (options?.storyId && scoreUpdates?.has(options.storyId)) {
      setLatestUpdate(scoreUpdates.get(options.storyId) || null);
    }
  }, [options?.storyId, scoreUpdates]);

  // Subscribe to updates — stable deps, no re-subscription churn
  useEffect(() => {
    const unsubscribe = onScoreUpdate((data: ScoreUpdateData) => {
      if (storyIdRef.current && data.storyId !== storyIdRef.current) {
        return;
      }

      setLatestUpdate(data);
      onUpdateRef.current?.(data);

      // Write the new score into the Apollo cache so the detail page
      // (which reads Post:{id}.score) reflects live WS updates without a
      // network refetch. cache.modify silently no-ops if the Post is absent.
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: 'Post',
          id: data.storyId,
        }),
        fields: { score: () => data.newScore },
      });
    });

    return unsubscribe;
  }, [onScoreUpdate]);

  // Helper to get score for a specific story
  const getScoreForStory = useCallback(
    (storyId: string): ScoreUpdateData | null => {
      return scoreUpdates?.get(storyId) || null;
    },
    [scoreUpdates],
  );

  return {
    latestUpdate,
    getScoreForStory,
    allUpdates: scoreUpdates,
  };
};
